import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import { directLoginUrl } from "@/lib/format";
import { X, Copy, Check, Eye, EyeOff, ExternalLink, ShieldAlert } from "lucide-react";

/**
 * One-shot credentials reveal modal.
 *
 *   * Fetches `GET /api/admin/clouds/{id}/credentials` on open (each
 *     mount → backend re-audits the operator).
 *   * Auto-hides the password + closes the modal after 30s of being
 *     visible (treated as a security-sensitive reveal — no caching).
 *   * Eye toggle lets the operator switch the password to dots while
 *     waiting for someone to look over their shoulder.
 *   * Copy buttons emit a momentary check-mark confirmation.
 *
 * The whole UX is fail-loud: if the worker hasn't pushed config yet
 * the backend returns 404 with a hint — surfaced here so the operator
 * understands they need to wait for provisioning.
 */
export default function CloudCredentialsModal({ cloudId, onClose }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pwVisible, setPwVisible] = useState(true);
    const [copied, setCopied] = useState(null);
    const [secondsLeft, setSecondsLeft] = useState(30);
    const [resetMode, setResetMode] = useState(false);
    const [ack, setAck] = useState(false);
    const [busy, setBusy] = useState(false);
    const [resetDone, setResetDone] = useState(false);
    const timerRef = useRef(null);

    // Initial fetch
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get(`/api/admin/clouds/${cloudId}/credentials`);
                if (cancelled) return;
                setData(r.data);
                setLoading(false);
            } catch (e) {
                if (cancelled) return;
                setErr(formatApiError(e));
                setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [cloudId]);

    // 30s auto-close countdown — paused while the operator is in the
    // reset-password flow (so it can't close mid-confirmation). Restarts
    // whenever data changes (e.g. after a successful reset).
    useEffect(() => {
        if (!data || resetMode || busy) return;
        const deadline = Date.now() + 30_000;
        setSecondsLeft(30);
        timerRef.current = setInterval(() => {
            const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
            setSecondsLeft(remaining);
        }, 250);
        return () => clearInterval(timerRef.current);
    }, [data, resetMode, busy]);

    // Trigger close once the countdown hits zero (split from the interval
    // tick so React's strict reducer rule isn't tripped).
    useEffect(() => {
        if (data && secondsLeft === 0 && !resetMode && !busy) {
            onClose();
        }
    }, [data, secondsLeft, resetMode, busy, onClose]);

    // Escape to close
    useEffect(() => {
        const handler = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    const copy = async (label, text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(label);
            setTimeout(() => setCopied(null), 1500);
        } catch (e) {
            // Ignore – non-secure contexts (HTTP local dev) don't have clipboard
        }
    };

    const doReset = async () => {
        setBusy(true);
        setErr(null);
        try {
            const r = await api.post(
                `/api/admin/clouds/${cloudId}/credentials/reset-password`);
            setData((d) => ({
                ...d,
                admin_user: r.data.admin_user || d.admin_user,
                admin_password: r.data.admin_password,
                config_version: r.data.config_version ?? d.config_version,
            }));
            setResetDone(true);
            setResetMode(false);
            setAck(false);
            setPwVisible(true);
        } catch (e) {
            setErr(formatApiError(e));
            setResetMode(false);
            setAck(false);
        } finally {
            setBusy(false);
        }
    };

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={onClose}
            data-testid="cloud-credentials-modal-overlay"
        >
            <div
                className="bg-zinc-950 border border-zinc-700 max-w-lg w-full shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                data-testid="cloud-credentials-modal"
            >
                <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
                    <div className="flex items-center gap-2">
                        <ShieldAlert size={16} className="text-amber-400" />
                        <h2 className="cm-h3">Initial Nextcloud Credentials</h2>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="cloud-credentials-close-button"
                        className="text-zinc-400 hover:text-zinc-200"
                        aria-label="close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-4 text-sm">
                    {loading && (
                        <div className="text-zinc-400" data-testid="cloud-credentials-loading">
                            Fetching credentials…
                        </div>
                    )}

                    {err && (
                        <div
                            className="bg-[#3a1010] border border-[#7a2a2a] p-3 text-xs text-[#ffaaaa] font-mono"
                            data-testid="cloud-credentials-error"
                        >
                            {err}
                        </div>
                    )}

                    {data && (
                        <>
                            <div className="bg-amber-900/20 border border-amber-700/50 p-3 text-xs text-amber-200 leading-relaxed">
                                These are the <strong>initial</strong> admin credentials
                                generated by the worker VM during installation. They are
                                stored encrypted in the NexTerminal DB. Please copy + hand
                                them to the customer over a secure channel. This dialog
                                auto-closes in{" "}
                                <span
                                    className="font-mono text-amber-100"
                                    data-testid="cloud-credentials-countdown"
                                >
                                    {secondsLeft}s
                                </span>
                                .
                            </div>

                            <CredRow
                                testidBase="cloud-credentials-url"
                                label="URL"
                                value={data.url}
                                copy={() => copy("url", data.url)}
                                copied={copied === "url"}
                                trailing={
                                    <a
                                        href={directLoginUrl(data.url)}
                                        target="_blank"
                                        rel="noreferrer noopener"
                                        className="text-cm-brand hover:text-cm-brand"
                                        title="Open local login (bypasses external auth)"
                                    >
                                        <ExternalLink size={13} />
                                    </a>
                                }
                            />

                            <CredRow
                                testidBase="cloud-credentials-username"
                                label="Admin User"
                                value={data.admin_user}
                                copy={() => copy("user", data.admin_user)}
                                copied={copied === "user"}
                            />

                            <CredRow
                                testidBase="cloud-credentials-password"
                                label="Admin Password"
                                value={
                                    data.admin_password == null
                                        ? "(not decryptable – please reset)"
                                        : pwVisible
                                            ? data.admin_password
                                            : "•".repeat(data.admin_password.length)
                                }
                                copy={data.admin_password == null ? undefined : () => copy("password", data.admin_password)}
                                copied={copied === "password"}
                                trailing={
                                    data.admin_password == null ? null : (
                                    <button
                                        type="button"
                                        onClick={() => setPwVisible((v) => !v)}
                                        data-testid="cloud-credentials-password-toggle"
                                        className="text-zinc-400 hover:text-zinc-100"
                                        title={pwVisible ? "hide" : "show"}
                                    >
                                        {pwVisible ? <EyeOff size={13} /> : <Eye size={13} />}
                                    </button>
                                    )
                                }
                            />

                            <div className="text-[11px] text-zinc-500 font-mono pt-2 border-t border-zinc-800">
                                config v{data.config_version ?? "—"}
                                {data.config_pushed_at
                                    ? ` pushed at ${new Date(data.config_pushed_at).toLocaleString()}`
                                    : " (no config-push yet)"}
                            </div>

                            {resetDone && (
                                <div
                                    className="text-xs text-emerald-300 bg-emerald-900/15 border border-emerald-700/40 px-3 py-2"
                                    data-testid="cloud-credentials-reset-done"
                                >
                                    ✓ New initial password generated and set in Nextcloud.
                                    Please copy above and hand over securely.
                                </div>
                            )}

                            <div className="pt-2 border-t border-zinc-800">
                                {!resetMode ? (
                                    <button
                                        type="button"
                                        onClick={() => { setResetMode(true); setResetDone(false); }}
                                        data-testid="cloud-credentials-reset-button"
                                        className="text-xs font-mono border border-amber-700/60 text-amber-300 px-3 py-1.5 hover:bg-amber-900/20 transition-colors"
                                    >
                                        Regenerate initial password…
                                    </button>
                                ) : (
                                    <div
                                        className="space-y-3 bg-[#2a1a05] border border-amber-700/50 p-3"
                                        data-testid="cloud-credentials-reset-confirm"
                                    >
                                        <div className="text-xs text-amber-200 leading-relaxed">
                                            This resets the admin password of{" "}
                                            <strong>{data.admin_user}</strong> in the
                                            Nextcloud instance <strong>anew</strong> (via{" "}
                                            <code className="text-amber-100">occ user:resetpassword</code>).
                                            The current password thereby becomes{" "}
                                            <strong>invalid</strong>. Data, DB and S3
                                            remain untouched.
                                        </div>
                                        <label className="flex items-start gap-2 text-xs text-amber-100 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={ack}
                                                onChange={(e) => setAck(e.target.checked)}
                                                data-testid="cloud-credentials-reset-ack"
                                                className="mt-0.5 accent-amber-500"
                                            />
                                            <span>
                                                I confirm: the previous admin password
                                                becomes invalid.
                                            </span>
                                        </label>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                disabled={!ack || busy}
                                                onClick={doReset}
                                                data-testid="cloud-credentials-reset-confirm-button"
                                                className="text-xs font-mono bg-amber-600 text-black px-3 py-1.5 hover:bg-amber-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                            >
                                                {busy ? "Resetting…" : "Reset permanently"}
                                            </button>
                                            <button
                                                type="button"
                                                disabled={busy}
                                                onClick={() => { setResetMode(false); setAck(false); }}
                                                data-testid="cloud-credentials-reset-cancel-button"
                                                className="text-xs font-mono border border-zinc-700 text-zinc-300 px-3 py-1.5 hover:bg-zinc-800 disabled:opacity-40 transition-colors"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}

function CredRow({ label, value, copy, copied, trailing, testidBase }) {
    return (
        <div className="space-y-1">
            <div className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</div>
            <div
                className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 px-3 py-2"
                data-testid={testidBase}
            >
                <span
                    className="flex-1 font-mono text-xs text-zinc-100 break-all"
                    data-testid={`${testidBase}-value`}
                >
                    {value}
                </span>
                <button
                    type="button"
                    onClick={copy}
                    data-testid={`${testidBase}-copy-button`}
                    className="text-zinc-400 hover:text-zinc-100"
                    title="copy"
                >
                    {copied ? (
                        <Check size={13} className="text-emerald-400" />
                    ) : (
                        <Copy size={13} />
                    )}
                </button>
                {trailing}
            </div>
        </div>
    );
}
